import { Ban } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Card } from "@/components/ui/card";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["blacklist"]) => void;
}

export function BlacklistTab({
  config,
  channels,
  roles,
  onChange,
}: Props) {
  const cfg = config.blacklist;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });

  return (
    <Section
      icon={Ban}
      title="Blacklist"
      description="Auto blacklist role, log channel, mod/admin roles."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          When enabled, blacklisted users get the configured role and actions are logged. Configure the{" "}
          <strong>blacklist role</strong> and <strong>log channel</strong> here; staff roles below can manage who is
          listed.
        </p>
      </div>

      <Card className="p-4 bg-muted/30">
        <h4 className="font-semibold mb-2 text-sm">Slash commands (staff)</h4>
        <div className="grid sm:grid-cols-2 gap-1.5 text-sm">
          <div className="flex items-baseline gap-2">
            <code className="text-primary font-mono text-xs">/blacklist</code>
            <span className="text-xs text-muted-foreground">Add user to blacklist (Admin)</span>
          </div>
          <div className="flex items-baseline gap-2">
            <code className="text-primary font-mono text-xs">/unblacklist</code>
            <span className="text-xs text-muted-foreground">Remove user from blacklist (Admin)</span>
          </div>
        </div>
      </Card>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Blacklist role" hint="Role given to blacklisted members (all other roles are removed)">
            <Select
              value={cfg.role_id || ""}
              onValueChange={(v) =>
                update("role_id", v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">(none)</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    @{r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Member role" hint="Role restored to the user when unblacklisted">
            <Select
              value={cfg.member_role_id || ""}
              onValueChange={(v) =>
                update("member_role_id", v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">(none)</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    @{r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Log channel">
            <ChannelPicker
              channels={channels}
              value={cfg.log_channel_id}
              onChange={(v) =>
                update("log_channel_id", v === "__none__" ? "" : v)
              }
            />
          </Field>
        </div>

        <Field label="Mod role (can add to blacklist)">
          <MultiSelect
            options={roles.map((r) => ({
              id: r.id,
              name: r.name,
              color: r.color,
            }))}
            value={cfg.mod_role_ids}
            onChange={(v) => update("mod_role_ids", v)}
            prefix="@"
          />
        </Field>

        <Field label="Admin role (can remove from blacklist)">
          <MultiSelect
            options={roles.map((r) => ({
              id: r.id,
              name: r.name,
              color: r.color,
            }))}
            value={cfg.admin_role_ids}
            onChange={(v) => update("admin_role_ids", v)}
            prefix="@"
          />
        </Field>
    </Section>
  );
}
